import { Component } from 'react';
import { Interpolate } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import moment from 'moment';

// Constants
import {
  PATIENT_NOT_VALIDATED,
  STATUS_ARRIVED,
  STATUS_BOOKED,
  STATUS_COMPLETED,
  STATUS_CONSULTATION_RESTARTED,
  STATUS_CONSULTATION_STARTED_AFTER_RADIOLOGY,
  STATUS_FINISH,
  STATUS_FINISHED,
  STATUS_IN_PROGRESS,
  STATUS_RADIOLOGY_FINISHED,
  STATUS_RADIOLOGY_FINISHED_DURING,
  STATUS_RADIOLOGY_START,
  STATUS_RADIOLOGY_STARTED,
  STATUS_RADIOLOGY_STARTED_DURING,
  STATUS_RADIOLOGY_START_DURING_CONSULTATION,
  STATUS_STARTED,
  STATUS_SUSPENDED
} from 'services/constants';

// Services & Helpers
import { transSteps, isValidTime } from '../../helper';
import { dateWithTimezone } from 'src/services/date';

// Components
import Btn from './Btn';
import ArrivalDelay from 'src/views/practitioner/components/ArrivalDelay';

// Styles & Images
import TickSvg from 'assets/images/appointment/tick.svg';
import styles from './styles';

const renderCommonTimeBlock = time =>
  isValidTime(time) && (
    <Interpolate
      i18nKey="date_time_1"
      date={dateWithTimezone(time, 'DD.MM.YYYY')}
      time={dateWithTimezone(time)}
    />
  );

const renderBookingTimeBlock = (time, creator) =>
  isValidTime(time) && (
    <Interpolate
      i18nKey="booked_by_username"
      date={dateWithTimezone(time, 'DD.MM.YYYY')}
      Username={`${creator.firstName} ${creator.lastName}`}
    />
  );

class AppointmentProgressItem extends Component {
  constructor() {
    super();
    this.state = { isDisableBtn: false, time: null };
    this.handlerButton = this.handlerButton.bind(this);
    this.renderTitle = this.renderTitle.bind(this);
    this.durationTime = this.durationTime.bind(this);
    this.showDuration = this.showDuration.bind(this);
  }

  durationTime() {
    const { step } = this.props;

    if (step.lastChangeDateTime) {
      const duration = moment().diff(dateWithTimezone(step.lastChangeDateTime, ''), 'minutes');
      this.setState({ time: duration });
    }
  }

  showDuration(status, step) {
    const { i18n, activeAppointment } = this.props;
    switch (status) {
      case STATUS_ARRIVED:
      case STATUS_RADIOLOGY_FINISHED: {
        if (step.block && step.block.name === 'arrive') {
          return <ArrivalDelay appointment={activeAppointment} withBrackets={true} />;
        }
        return null;
      }
      case STATUS_CONSULTATION_RESTARTED:
      case STATUS_RADIOLOGY_STARTED:
      case STATUS_STARTED: {
        if (step.lastChangeDateTime && step.status === STATUS_IN_PROGRESS && this.state.time) {
          return (
            <span>
              ({this.state.time} {i18n.t('min_ago')})
            </span>
          );
        }
        return null;
      }
      default:
        return null;
    }
  }

  componentDidMount() {
    this.durationTime();
    this.interval = setInterval(this.durationTime, 60000);
  }

  componentWillUnmount() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  handlerButton(trigger) {
    const {
      actions,
      activeAppointment,
      openPaymentModal,
      openRadiologyModal,
      openVerifyModal,
      patient,
      step,
      history
    } = this.props;
    const {
      appointment: {
        entity: { state, externalAppointmentId, id: historyId }
      },
      id,
      invoice
    } = activeAppointment;

    if (state !== STATUS_FINISHED && trigger !== STATUS_RADIOLOGY_START) {
      this.setState({ isDisableBtn: true });
    }

    if (
      trigger === STATUS_RADIOLOGY_START ||
      trigger === STATUS_RADIOLOGY_START_DURING_CONSULTATION
    ) {
      openRadiologyModal && openRadiologyModal();
      return;
    }

    if (trigger === STATUS_FINISH) {
      const link =
        invoice && invoice.id
          ? `/practitioner/appointments/${id}/create-invoice/${invoice.id}`
          : `/practitioner/appointments/${id}/create-invoice`;
      history.push(link);
      return false;
    }

    switch (state) {
      case STATUS_BOOKED:
      case STATUS_ARRIVED:
      case STATUS_STARTED:
      case STATUS_SUSPENDED:
      case STATUS_RADIOLOGY_STARTED:
      case STATUS_RADIOLOGY_FINISHED:
      case STATUS_RADIOLOGY_STARTED_DURING:
      case STATUS_RADIOLOGY_FINISHED_DURING: {
        actions.triggerWorkflow(trigger, externalAppointmentId, historyId);
        this.setState({ isDisableBtn: false });
        if (
          patient &&
          patient.umbi &&
          patient &&
          PATIENT_NOT_VALIDATED === patient.umbi.status &&
          step.block.name === 'arrive'
        ) {
          openVerifyModal && openVerifyModal();
        }
        break;
      }
      case STATUS_FINISHED: {
        openPaymentModal && openPaymentModal();
        break;
      }
    }
  }

  renderTitle(isActive) {
    const { i18n, step } = this.props;

    switch (step.state) {
      case STATUS_SUSPENDED:
        return i18n.t(STATUS_SUSPENDED);
      case STATUS_STARTED:
        return i18n.t('appointment_started');
      case STATUS_CONSULTATION_RESTARTED:
        return i18n.t(STATUS_CONSULTATION_RESTARTED);
      case STATUS_FINISHED:
        return i18n.t('appointment_finished');
      case STATUS_CONSULTATION_STARTED_AFTER_RADIOLOGY:
        return i18n.t('appointment_started');
      default: {
        if (!isValidTime(step.lastChangeDateTime) || isActive) {
          return i18n.t(transSteps[step.block.name].title);
        } else if (step.status === STATUS_COMPLETED) {
          return i18n.t(transSteps[step.block.name].pastTitle);
        }
      }
    }
  }

  renderTime() {
    const {
      step,
      activeAppointment: {
        creator,
        createdDateTime,
        appointment
      }
    } = this.props;

    if (step.block.name === 'booking') {
      return renderBookingTimeBlock(createdDateTime, creator);
    } else if (step.block.name === 'arrive' && appointment && appointment.entity && appointment.entity.arrivalDateTime
      && step.lastChangeDateTime) {
      return renderCommonTimeBlock(appointment.entity.arrivalDateTime);
    } else {
      return renderCommonTimeBlock(step.lastChangeDateTime);
    }
  }

  render() {
    const { step, activeAppointment } = this.props;
    const { appointment } = activeAppointment;
    const state = appointment && appointment.entity && appointment.entity.state;
    const isActive = step.status === STATUS_IN_PROGRESS;
    const isFinished = step.status === STATUS_COMPLETED;
    const inProcess =
      state === STATUS_STARTED ||
      state === STATUS_RADIOLOGY_STARTED ||
      state === STATUS_CONSULTATION_RESTARTED ||
      state === STATUS_CONSULTATION_STARTED_AFTER_RADIOLOGY;

    const { isDisableBtn } = this.state;

    return (
      <styles.ProgressBarItem>
        {/* ──── Circles ──────────────────────────────────────────────────────────────────── */}
        <styles.Circle className="counter" isActive={isActive}>
          {isFinished ? <img src={TickSvg} alt="tick" /> : <span>{step.number}</span>}
        </styles.Circle>

        {/* ──── Content block ──────────────────────────────────────────────────────────────*/}
        <styles.Step isActive={isActive} isFinished={isFinished}>
          <styles.Block isActive={isActive} isFinished={isFinished} inProcess={inProcess}>
            <span>{this.renderTitle(isActive, state)}</span>
            <div>
              {this.renderTime()}
              {this.showDuration(state, step)}
            </div>
          </styles.Block>

          {isActive && (
            <styles.ButtonsBlock>
              {step.triggers.map(trigger => (
                <Btn
                  key={trigger}
                  state={state}
                  trigger={trigger}
                  disabled={isDisableBtn}
                  handlerButton={this.handlerButton}
                  activeAppointment={activeAppointment}
                />
              ))}
            </styles.ButtonsBlock>
          )}
        </styles.Step>
      </styles.ProgressBarItem>
    );
  }
}

export default withRouter(AppointmentProgressItem);
